import React, { useCallback } from 'react';

import {
    applySidebarWidth,
    SIDEBAR_MAX_WIDTH,
    SIDEBAR_MIN_WIDTH
} from '../../../api/userPreferences/useSidebarWidth';

/** Dragged narrower than this, the sidebar snaps to the icon rail. */
export const SIDEBAR_SNAP_WIDTH = 120;

type Options = {
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
    /** True while a drag is in progress, so the layout can drop its width animation. */
    onResizingChange: (resizing: boolean) => void;
    /** The width to save in the user's preferences; null returns to the default. */
    onWidthCommit: (width: number | null) => void;
};

/**
 * Drag the sidebar's right edge to resize it, as in most apps with a side rail.
 * Dragging below SIDEBAR_SNAP_WIDTH collapses to the icon rail, and dragging out
 * of the rail opens it again. The width is saved in the user's preferences
 * (see useSidebarWidth); double-click the edge to return to the default.
 */
export const useSidebarResize = ({ collapsed, setCollapsed, onResizingChange, onWidthCommit }: Options) => {
    const startDrag = useCallback(
        (event: React.PointerEvent) => {
            if (event.button !== 0) return;
            event.preventDefault();

            // The app is zoomed on #root (display size); pointer positions are not.
            const zoom = Number(getComputedStyle(document.documentElement).getPropertyValue('--app-zoom')) || 1;
            let isCollapsed = collapsed;
            let lastWidth: number | null = null;

            onResizingChange(true);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            const move = (moveEvent: PointerEvent) => {
                // The sidebar starts at the window's left edge, so the pointer's x is its width.
                const x = moveEvent.clientX / zoom;
                if (x < SIDEBAR_SNAP_WIDTH) {
                    if (!isCollapsed) {
                        isCollapsed = true;
                        setCollapsed(true);
                    }
                    return;
                }
                if (isCollapsed) {
                    isCollapsed = false;
                    setCollapsed(false);
                }
                lastWidth = Math.round(Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, x)));
                applySidebarWidth(lastWidth);
            };

            const up = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                onResizingChange(false);
                if (lastWidth !== null) onWidthCommit(lastWidth);
            };

            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
        },
        [collapsed, setCollapsed, onResizingChange, onWidthCommit]
    );

    const resetWidth = useCallback(() => {
        applySidebarWidth(null);
        onWidthCommit(null);
    }, [onWidthCommit]);

    return { startDrag, resetWidth };
};
